import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'

import { successStaleTime } from '@/shared/api/utils/query'
import { useCurrentTenant } from '@/shared/auth/store'
import { time } from '@/shared/datetime/utils/time'

import { fetchCollector, fetchCollectors, fetchLogSource } from './api'

/**
 * Cache keys for the Network Collectors sub-feature.
 *
 * Its own root (`['collectors']`) rather than a branch of `integrationKeys`: a
 * Collector is not a Cloud integration, and hanging it off the integrations root
 * would make `invalidateIntegrations()` drop this cache for reasons that have
 * nothing to do with it.
 */
export const collectorKeys = {
	all: ['collectors'] as const,

	lists: () => [...collectorKeys.all, 'list'] as const,
	/**
	 * Keyed by tenant even though the fixture ignores it — the real endpoint will
	 * be tenant-scoped, and a key that omits the tenant serves one tenant's
	 * Collectors to the next after a tenant switch.
	 */
	list: (tenant: string) => [...collectorKeys.lists(), tenant] as const,

	details: () => [...collectorKeys.all, 'detail'] as const,
	/** Tenant-scoped for the same reason the list is — see {@link collectorKeys.list}. */
	detail: (tenant: string, collectorId: string) =>
		[...collectorKeys.details(), tenant, collectorId] as const,

	sources: () => [...collectorKeys.all, 'source'] as const,
	/**
	 * One Log Source, keyed by the **pair** that identifies it. A Log Source has
	 * no identity without its Collector, so the source id alone is not a key —
	 * and keying it that way would let a source cached under one Collector answer
	 * for the same id under another.
	 */
	source: (tenant: string, collectorId: string, sourceId: string) =>
		[...collectorKeys.sources(), tenant, collectorId, sourceId] as const,
}

export const collectorQueries = {
	/**
	 * The tenant's Collectors, each with the Log Sources hosted on it.
	 *
	 * Short `staleTime` because the whole point of the surface is liveness: a
	 * "last seen 3s ago" that is itself five minutes old is worse than no reading.
	 */
	list: (tenant: string) =>
		queryOptions({
			queryKey: collectorKeys.list(tenant),
			queryFn: ({ signal }) => fetchCollectors(signal),
			staleTime: ({ state }) => successStaleTime(state, 30, 's'),
			gcTime: time(5, 'm'),
			// No `enabled` guard: the sole consumer is `useSuspenseQuery`, which
			// forces `enabled: true` and would silently ignore one — it would read
			// as a guard while doing nothing. The tenant check that matters is the
			// route loader's, before it prefetches.
		}),

	/**
	 * One Collector and the Log Sources hosted on it.
	 *
	 * Its own query rather than a `select` over the cached list: the detail page
	 * is reachable directly by URL, and deriving it from the list would make a
	 * pasted link fetch every Collector in the tenant to render one. The real
	 * endpoint is a by-id read for the same reason.
	 *
	 * Same short `staleTime` as the list — the page leads with `Last seen`.
	 */
	detail: (tenant: string, collectorId: string) =>
		queryOptions({
			queryKey: collectorKeys.detail(tenant, collectorId),
			queryFn: ({ signal }) => fetchCollector(collectorId, signal),
			staleTime: ({ state }) => successStaleTime(state, 30, 's'),
			gcTime: time(5, 'm'),
		}),

	/**
	 * One Log Source, with the name and liveness of the Collector hosting it.
	 *
	 * Its own query rather than a `select` over the Collector's detail query: the
	 * manage page is reachable directly by URL, and the real endpoint will be a
	 * by-id read. Same short `staleTime` — the page leads with `last log
	 * received`.
	 */
	source: (tenant: string, collectorId: string, sourceId: string) =>
		queryOptions({
			queryKey: collectorKeys.source(tenant, collectorId, sourceId),
			queryFn: ({ signal }) => fetchLogSource(collectorId, sourceId, signal),
			staleTime: ({ state }) => successStaleTime(state, 30, 's'),
			gcTime: time(5, 'm'),
		}),
}

/** The Collectors list for the current tenant. Suspends. */
export function useCollectors() {
	const tenant = useCurrentTenant()
	return useSuspenseQuery(collectorQueries.list(tenant))
}

/**
 * One Collector for the current tenant. Suspends.
 *
 * `data` is `null` when no Collector has that id — the page's not-found state,
 * not an error (see `fetchCollector`).
 */
export function useCollector(collectorId: string) {
	const tenant = useCurrentTenant()
	return useSuspenseQuery(collectorQueries.detail(tenant, collectorId))
}

/**
 * One Log Source for the current tenant. Suspends.
 *
 * `data` is `null` when the `(collector, source)` pair matches nothing — the
 * page's not-found state, not an error (see `fetchLogSource`).
 */
export function useLogSource(collectorId: string, sourceId: string) {
	const tenant = useCurrentTenant()
	return useSuspenseQuery(
		collectorQueries.source(tenant, collectorId, sourceId),
	)
}
